import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sun, Moon, Menu, X, ArrowRight } from 'lucide-react';
import { useTheme } from '../core/theme/ThemeProvider';
import { WorqlyMark } from '../design-system/components/WorqlyMark';

/**
 * MarketingNav
 * --------------------------------------------------------------
 * Sticky top nav for marketing pages:
 *   - Worqly wordmark (left)
 *   - Primary nav links (Features, Pricing, Customers, Changelog, About)
 *   - Theme toggle + Sign-in + Start-free (right)
 *   - Full-screen mobile overlay on hamburger
 *
 * All in-product paths use react-router <Link>. Light + dark
 * variants are explicit so the nav reads correctly on both.
 */

const NAV: Array<{ label: string; to: string }> = [
  { label: 'Features',  to: '/features'  },
  { label: 'Pricing',   to: '/pricing'   },
  { label: 'Customers', to: '/customers' },
  { label: 'Blog',      to: '/blog'      },
  { label: 'Docs',      to: '/docs'      },
  { label: 'Changelog', to: '/changelog' },
  { label: 'About',     to: '/about'     },
];

function ThemePill({ className = '' }: { className?: string }) {
  const { resolved, setMode } = useTheme();
  // Show the icon for the theme that's currently rendered — Sun in light,
  // Moon in dark. Click flips to the opposite. We deliberately avoid
  // exposing 'system' here because the cycling Monitor icon confused
  // users who couldn't tell what theme they were actually in.
  const Icon = resolved === 'light' ? Sun : Moon;
  const next = resolved === 'light' ? 'dark' : 'light';
  const label = resolved === 'light' ? 'Switch to dark mode' : 'Switch to light mode';
  return (
    <button
      type="button"
      onClick={() => setMode(next)}
      aria-label={label}
      title={label}
      className={
        'inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors ' +
        'bg-white/70 text-ink shadow-pill hover:bg-white ' +
        'dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:shadow-none ' +
        className
      }
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function MarketingNav() {
  const [open, setOpen] = React.useState(false);
  const { pathname } = useLocation();

  // Close mobile menu on route change.
  React.useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Lock body scroll when mobile menu is open.
  React.useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  return (
    <header className="sticky top-0 z-40">
      {/* Translucent backdrop */}
      <div
        aria-hidden
        className={
          'absolute inset-0 -z-10 border-b ' +
          'bg-white/75 backdrop-blur-xl border-ink/8 ' +
          'dark:bg-ink-900/80 dark:border-white/8'
        }
      />

      <nav
        className="max-w-site mx-auto px-6 lg:px-8 h-16 flex items-center justify-between"
        aria-label="Primary"
      >
        <Link
          to="/"
          className="flex items-center gap-2 group focus-coral rounded-md"
          aria-label="Worqly home"
        >
          <WorqlyMark size="md" />
        </Link>

        {/* Desktop links */}
        <ul className="hidden lg:flex items-center gap-1">
          {NAV.map((it) => {
            const active = pathname === it.to || pathname.startsWith(it.to + '/');
            return (
              <li key={it.to}>
                <Link
                  to={it.to}
                  className={
                    'inline-flex items-center px-3.5 py-2 rounded-full text-[13px] font-semibold transition-colors ' +
                    (active
                      ? 'text-ink bg-white/70 shadow-pill dark:text-white dark:bg-white/10'
                      : 'text-ink/70 hover:text-ink hover:bg-white/60 dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10')
                  }
                >
                  {it.label}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-2">
          <ThemePill />
          <Link
            to="/login"
            className="inline-flex items-center px-3.5 h-9 rounded-full text-[13px] font-semibold text-ink/80 hover:text-ink hover:bg-white/60 dark:text-white/80 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-coral text-white text-[13px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
          >
            Start free
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full bg-white text-ink shadow-pill dark:bg-white/10 dark:text-white dark:shadow-none focus-coral"
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </nav>

      {/* Mobile full-screen menu */}
      {open && (
        <div
          id="marketing-mobile-menu"
          className="lg:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-white dark:bg-ink-900 overflow-y-auto"
        >
          <div className="px-6 py-6 flex flex-col gap-2">
            {NAV.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                className="flex items-center justify-between py-3 text-[18px] font-semibold text-ink dark:text-white border-b border-ink/8 dark:border-white/10"
              >
                <span>{it.label}</span>
                <ArrowRight className="h-4 w-4 text-ink/40 dark:text-white/40" />
              </Link>
            ))}

            <div className="pt-6 mt-2 grid grid-cols-2 gap-3">
              <Link
                to="/login"
                className="flex items-center justify-center h-12 rounded-full bg-white text-ink ring-1 ring-ink/10 dark:bg-white/10 dark:text-white dark:ring-white/10 text-[14px] font-semibold"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="flex items-center justify-center h-12 rounded-full bg-coral text-white text-[14px] font-semibold shadow-cta"
              >
                Start free
              </Link>
            </div>

            <div className="pt-6 flex items-center justify-between">
              <span className="text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
                Theme
              </span>
              <ThemePill />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
